import {Component, Watch, Emit, Vue, Prop, Inject} from 'vue-property-decorator';
import { debounce } from 'lodash';
import { Scroller } from './scroller';
// import { DropLayer } from './wormhole';
import iconListSearch from '~/assets/inline-svg/icon_list-search.svg?inline';
import { StackSystem } from './stack-system';
import { SearchListModal } from './modals/search-list-modal';
import './autosuggest.scss'

const HIDE_TIMEOUT = 280; //delay hide results on blur
const DEBOUNCE_SEARCH = 350;

const KEYS = {
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  TAB: 9,
  RETURN: 13,
  ESC: 27,
  APPLE: 91,
  SHIFT: 16,
  ALT: 17,
  CTRL: 18
};

@Component
class AutoSuggest extends Vue {

  @Inject() rootModalStack: StackSystem;

  @Prop({ type: String, required: false, default: "Rechercher" }) placeholder: string;
  @Prop({ type: Function, required: true }) searchAction: Function;
  @Prop({ type: Function, required: false, default: () => [] }) defaultsAction: Function;
  @Prop({ type: Number, required: false, default: 5 }) limit: number;
  @Prop({ type: Number, required: false, default: 1 }) minChar: number;
  @Prop({ type: Number, required: false, default: 100 }) maxDisplayedResults: number;
  @Prop({ type: Boolean, required: false, default: false }) withResetButton: boolean;

  protected cache: any = "";
  protected inputText: string = "";
  protected currentIdx: number = 0;
  protected results: Array<any> = [];
  protected fading: boolean = false;
  protected displayResults: boolean = false;
  protected searchDebouncer: any = debounce(this.search.bind(this), DEBOUNCE_SEARCH);

  @Watch("displayResults")
  onDisplayResultsChanged() {
    if (this.displayResults && this.fading) {
      this.fading = false;
    }
  }

  /* actions */

  @Emit('focus')
  async onFocus() {
    if (this.cache < this.minChar) {
      this.results = await this.defaultsAction();
      this.displayResults = true;
    } else {
      this.results.length && (this.displayResults = true);
    }
  }

  @Emit('keydown')
  onKeydown(e) {
    switch (e.keyCode) {
      case KEYS.TAB:
        e.preventDefault();
        if (this.results.length && this.currentIdx != null && this.currentIdx >= 0) {
          this.onSelect(this.results[this.currentIdx]);
        }
        break;
      case KEYS.RETURN:
        e.preventDefault();
        if (this.results.length && this.currentIdx != null && this.currentIdx >= 0) {
          this.onSelect(this.results[this.currentIdx]);
        }
        break;
      case KEYS.ESC:
        e.preventDefault();
        this.hide();
        break;
      case KEYS.DOWN:
        e.preventDefault();
        if (this.results.length > 0) {
          if (this.currentIdx !== null && this.currentIdx < this.results.length - 1) {
            // what if one resutl?
            this.currentIdx++;
          } else if (this.currentIdx === null) {
            this.currentIdx = 0;
          }
        }
        break;
      case KEYS.UP:
        e.preventDefault();
        if (this.results.length > 0) {
          if (this.currentIdx > 0) {
            this.currentIdx--;
          } else if (this.currentIdx === 0) {
            this.currentIdx = null;
          }
        }
        break;
    }
    return e;
  }


  @Emit('input')
  onInput(e) {
    this.inputText = e.target.value;
    let val = e.target.value.trim();
    if (val !== this.cache) {
      this.cache = val;
    }
    if (!val || val.length < this.minChar) {
      this.onResults([]);
    } else {
      this.searchDebouncer();
    }
  }

  @Emit('blur')
  onBlur() {
    this.displayResults && this.hide(HIDE_TIMEOUT, false);
  }

  @Emit('results')
  async onResults(features) {
    if (this.cache.length >= this.minChar) {
      this.results = features;
    } else {
      this.results = await this.defaultsAction();
    }
    this.currentIdx = 0;
    this.displayResults = this.results.length > 0;
    return features;
  }

  @Emit('selected')
  async onSelect(selection, setIdx = false) {
    setIdx && (this.currentIdx = this.results.indexOf(selection));
    if (selection) {
      this.inputText = "";
      this.results = await this.defaultsAction();
      this.hide();
      return selection;
    }
    return null;
  }

  handleClickReset() {
    this.inputText = "";
    this.$emit("selected", null);
  }

  /* --- */

  hide(timeout?, blur = true) {
    this.fading = true;
    setTimeout(() => {
      this.fading = false;
      this.displayResults = false;
      blur && (this.$refs.input as any).blur();
      this.$emit("hide");
    }, timeout || HIDE_TIMEOUT);
  }

  search() {
    this.searchAction(this.cache).then(features => this.onResults(features));
  }

  openModal() {
    this.rootModalStack.push(destroy => (
      <SearchListModal
        onCancel={() => destroy()}
        onCommit={(val) => {
          val.map(v => this.onSelect({label: v}, true))
          destroy()
        }}
      />
    ));
  }

  renderResult(r, idx) {
    let highlighted = this.currentIdx === idx ? 'highlighted' : '';
    return (
      <div class={`result-row ${highlighted}`} on-click={() => this.onSelect(r, true)}>
        {this.$scopedSlots.row(r)}
      </div>
    );
  }

  render() {
    return (
      <div class="autosuggest">
        <div class="input-filter">
          <input
            ref="input"
            value={this.inputText}
            placeholder={this.placeholder}
            on-focus={this.onFocus}
            on-blur={this.onBlur}
            on-keydown={this.onKeydown}
            on-input={this.onInput}
          />
          {this.withResetButton && (
            <div class="reset-filter is-clickable" on-click={this.handleClickReset}>x</div>
          )}
          <button class="btn--icon icon--primary icon auto-size has-tooltip-left" domPropsInnerHTML={iconListSearch} onClick={() => this.openModal()} data-tooltip="Rechercher et ajouter à la sélection à partir d'une liste"/>
        </div>
        {this.displayResults && (
          //FIXME <DropLayer trigger={(this.$refs.input as any)}>
            <Scroller class="autosuggest--scroller">
              <div ref="dropdown"
                class={`autosuggest__results ${this.fading ? 'is-fading' : ''}`}
              >
                {this.results.map(this.renderResult)}
                {this.results.length > this.maxDisplayedResults && (
                  <div class={`result-row no-highlight`}>
                    ...
                  </div>
                )}
              </div>
            </Scroller>
          // </DropLayer>
        )}
      </div>
    );
  }
};

export default AutoSuggest;

// VUE JSX HOT LOADER //
if (module.hot) require("/usr/src/app/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-5f201fbf/autosuggest.tsx" });